import { ExternalLink, Hash, Users } from 'lucide-react';
import { Badge } from '../ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { UserAvatar } from './UserAvatar';
import { cn } from '../../lib/utils';

/**
 * Display data for one resolved external entity. Structural on purpose:
 * `@reflex/plugin-api`'s `ExternalRefProfile` satisfies it without the
 * design system depending on the plugin contract package.
 */
export interface ExternalRefChipProfile {
  displayName: string;
  /** Secondary muted line (a channel topic, a team key). */
  subtitle?: string;
  /** Provider handle when one exists (a Slack `@name`). Users only. */
  handle?: string;
  email?: string;
  avatarUrl?: string;
  /** Deep link to the entity in the provider's own app. */
  url?: string;
}

export interface ExternalRefChipProps {
  /** Raw provider id — the fallback label and the popover's mono line. */
  id: string;
  /** Human provider name for popover copy ('Slack', 'Linear'). */
  providerLabel: string;
  /**
   * Entity kind for popover copy and the leading glyph: 'channel' and
   * 'team' get their kind's icon in a squared tile; everything else is a
   * person and gets the round avatar (image, initial, or person glyph).
   */
  kindLabel?: string;
  /** Resolved profile. Absent renders the raw-id fallback chip. */
  profile?: ExternalRefChipProfile | null;
  /** True while resolution is in flight — mutes the fallback chip. */
  pending?: boolean;
  /**
   * Text scale of the badge. `sm` (10px) sits in dense chip strips like
   * trigger summaries; `md` (12px) matches the value rhythm of payload
   * key/value lists so the chip doesn't read undersized next to siblings.
   */
  size?: 'sm' | 'md';
  className?: string;
  'data-testid'?: string;
}

/** Kind glyph: squared icon tile for containers (channels, teams), round
 * avatar for people. Sized to sit inside the chip or the popover header. */
function RefGlyph({
  kindLabel,
  profile,
  scale,
}: {
  kindLabel: string;
  profile?: ExternalRefChipProfile | null;
  scale: 'chip-sm' | 'chip-md' | 'popover';
}) {
  const KindIcon = kindLabel === 'channel' ? Hash : kindLabel === 'team' ? Users : null;
  const tile = {
    'chip-sm': 'h-3 w-3 rounded-[3px] [&>svg]:h-2 [&>svg]:w-2',
    'chip-md': 'h-4 w-4 rounded-[4px] [&>svg]:h-2.5 [&>svg]:w-2.5',
    popover: 'h-8 w-8 rounded-md [&>svg]:h-4 [&>svg]:w-4',
  }[scale];
  if (KindIcon) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'flex shrink-0 items-center justify-center bg-muted text-muted-foreground ring-1 ring-border',
          tile,
        )}
      >
        <KindIcon />
      </span>
    );
  }
  const avatar = {
    'chip-sm': { size: 'xs' as const, className: 'h-3 w-3' },
    'chip-md': { size: 'xs' as const, className: undefined },
    popover: { size: 'md' as const, className: undefined },
  }[scale];
  return (
    <UserAvatar
      size={avatar.size}
      className={avatar.className}
      userName={profile?.displayName}
      avatarUrl={profile?.avatarUrl}
    />
  );
}

/**
 * Compact badge for an entity of an external service (a Slack member or
 * channel, a Linear assignee or team): kind glyph + name with a hover
 * popover carrying the full profile and a deep link into the provider.
 * Degrades to the raw id in monospace while unresolved, so surfaces can
 * render it before (or without) a successful lookup.
 */
export function ExternalRefChip({
  id,
  providerLabel,
  kindLabel = 'user',
  profile,
  pending,
  size = 'sm',
  className,
  'data-testid': dataTestId = 'external-ref-chip',
}: ExternalRefChipProps) {
  // Pill metrics match the sibling chips of trigger-summary strips (`sm`)
  // and the 12px value rhythm of payload lists (`md`).
  const badgeSize = size === 'sm' ? 'gap-1 px-2 py-0.5 text-[10px]' : 'gap-1.5 px-2 py-0.5 text-xs';
  const glyphScale = size === 'sm' ? 'chip-sm' : 'chip-md';

  if (!profile) {
    return (
      <Badge
        variant="outline"
        data-testid={dataTestId}
        data-state={pending ? 'pending' : 'unresolved'}
        aria-label={`${providerLabel} ${kindLabel} ${id}`}
        className={cn(
          'rounded-full border-border/60 bg-muted/40 font-medium text-foreground/80',
          badgeSize,
          pending && 'animate-pulse text-muted-foreground',
          className,
        )}
      >
        <RefGlyph kindLabel={kindLabel} scale={glyphScale} />
        <span className="max-w-[160px] truncate font-mono">{id}</span>
      </Badge>
    );
  }

  const subline = profile.handle
    ? `@${profile.handle}`
    : (profile.subtitle ?? profile.email ?? null);
  // Sink-side guard: profiles originate from third-party APIs, so only an
  // https URL may become the popover's link (never javascript:/data:).
  const linkUrl = profile.url?.startsWith('https://') ? profile.url : null;
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          tabIndex={0}
          aria-label={`${providerLabel} ${kindLabel} ${profile.displayName}`}
          data-testid={dataTestId}
          data-state="resolved"
          className="inline-flex items-center rounded-full align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Badge
            variant="outline"
            className={cn(
              'cursor-default rounded-full border-border/60 bg-muted/40 font-medium text-foreground/80 hover:bg-muted',
              badgeSize,
              className,
            )}
          >
            <RefGlyph kindLabel={kindLabel} profile={profile} scale={glyphScale} />
            <span className="max-w-[160px] truncate">{profile.displayName}</span>
          </Badge>
        </span>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-64" data-testid="external-ref-chip-popover">
        <div className="flex items-start gap-2.5">
          <RefGlyph kindLabel={kindLabel} profile={profile} scale="popover" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{profile.displayName}</p>
            {subline && <p className="truncate text-xs text-muted-foreground">{subline}</p>}
            {profile.handle && profile.email && (
              <p className="truncate text-xs text-muted-foreground">{profile.email}</p>
            )}
            <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              {providerLabel} {kindLabel}
            </p>
            {linkUrl && (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`View in ${providerLabel}`}
                className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
              >
                View in {providerLabel}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
